import {Component, inject, model, signal, viewChild} from '@angular/core';
import {DatePipe, NgClass} from '@angular/common';
import {Subscription} from 'rxjs';
import {Dialog} from 'primeng/dialog';
import {Button} from 'primeng/button';
import {Chip} from 'primeng/chip';
import {TableModule} from 'primeng/table';
import {BasicTaDetails} from '../../../api';
import {TaSearchDatasource} from '../../../datasources/ta-search-datasource';
import {TaNavigationService} from '../../../services/ta-navigation.service';
import {ErrorMessageService} from '../../../services/error-message.service';
import {AdvancedSearchParameters, emptyAdvancedSearchParameters, toTAStatusText} from '../../../util/app.types';
import {PrepaidIndicatorComponent} from '../prepaid-indicator/prepaid-indicator.component';
import {AttachmentIndicatorComponent} from '../attachment-indicator/attachment-indicator.component';
import {TaCommentCount} from '../ta-comments/ta-comment-count';
import {SubmissionSearchBarComponent} from '../ta-list-tables/submission-search-bar/submission-search-bar.component';

@Component({
  selector: 'quick-search',
  imports: [Dialog, Button, Chip, TableModule, DatePipe, NgClass, PrepaidIndicatorComponent, AttachmentIndicatorComponent, TaCommentCount, SubmissionSearchBarComponent],
  templateUrl: './quick-search.component.html',
})
export class QuickSearchComponent {
  private taSearchDatasource = inject(TaSearchDatasource);
  private taNavigationService = inject(TaNavigationService);
  private errorMessageService = inject(ErrorMessageService);

  visible = model<boolean>(false);

  advancedEnabled = false;
  searchStr = '';
  advancedSearchParameters: AdvancedSearchParameters = emptyAdvancedSearchParameters();
  results = signal<BasicTaDetails[]>([]);
  totalCount = signal(0);
  hasSearched = signal(false);
  loading = signal(false);
  selectedId = signal<string | undefined>(undefined);

  protected readonly toTAStatusText = toTAStatusText;

  private searchBar = viewChild(SubmissionSearchBarComponent);
  // Last executed search; Load More and reopen use this, not the form's current values.
  private searched?: {advanced: boolean, term: string, params: AdvancedSearchParameters};
  private currentPage = 0;
  private request?: Subscription;

  // Kept results go stale once a TA is changed elsewhere, so the last search re-runs on reopen.
  onShow(): void {
    this.focusInput();
    if (this.searched) {
      this.currentPage = 0;
      this.fetch(false, true);
    }
  }

  focusInput(): void {
    this.searchBar()?.focusInput();
  }

  runSearch(): void {
    const term = this.searchStr.trim();
    if (!this.advancedEnabled && !term) {
      return;
    }
    this.searched = {advanced: this.advancedEnabled, term, params: {...this.advancedSearchParameters}};
    this.currentPage = 0;
    this.fetch(false);
  }

  loadMore(): void {
    if (this.loading() || !this.canLoadMore()) {
      return;
    }
    this.currentPage++;
    this.fetch(true);
  }

  canLoadMore(): boolean {
    return this.results().length < this.totalCount();
  }

  clear(): void {
    this.request?.unsubscribe();
    this.searchStr = '';
    this.advancedSearchParameters = emptyAdvancedSearchParameters();
    this.searched = undefined;
    this.currentPage = 0;
    this.results.set([]);
    this.totalCount.set(0);
    this.hasSearched.set(false);
    this.loading.set(false);
    this.selectedId.set(undefined);
    this.focusInput();
  }

  open(ta: BasicTaDetails): void {
    this.selectedId.set(ta.id ?? undefined);
    this.visible.set(false);
    this.taNavigationService.open(ta);
  }

  // A newer search or load supersedes any request still in flight.
  private fetch(append: boolean, keepSelection = false): void {
    const searched = this.searched!;
    this.request?.unsubscribe();
    this.loading.set(true);
    const request$ = searched.advanced
      ? this.taSearchDatasource.quickAdvancedSearchSubmissions(searched.params, this.currentPage)
      : this.taSearchDatasource.quickSearchSubmissions(searched.term, this.currentPage);
    this.request = request$.subscribe({
      next: result => {
        const page = result.results ?? [];
        this.results.update(current => append ? [...current, ...page] : page);
        this.totalCount.set(result.totalCount ?? 0);
        this.hasSearched.set(true);
        if (!append && !keepSelection) {
          this.selectedId.set(undefined);
        }
        this.loading.set(false);
      },
      error: err => {
        console.error(err);
        if (append) {
          this.currentPage--;
        }
        this.errorMessageService.showTemporaryError('TA search failed. ' + (err?.message ?? ''), 5000);
        this.loading.set(false);
      },
    });
  }
}
